import { SkyBadge, SkyButton, SkyCodeBlock, SkyText } from "@skypointcloud/skypoint-ui";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { chipOf, isComingSoon, readinessReasonLine } from "@/hooks/use-destination-readiness";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { formatInstant } from "@/lib/time/displayTime";
import type { DestinationReadinessView } from "@shared/types/instances";
import type { DestinationTestView } from "@shared/types/instancesAdmin";
import { TEST_CREATES_SCHEMA_NOTE } from "@shared/warehouse/destinationTestAdvice";

/**
 * #923: the destination's state and its test, on the Warehouse tab. A destination is Draft until
 * a Test passes; while it is not Ready nothing runs. Test (Draft) may move it to Ready or back to
 * Draft; Check now (Ready) only records the check, so a failing check on a Ready destination warns
 * and the chip stays Ready. Save and test saves first, then tests a Draft or checks a Ready one
 * (design review change 6: a save that leaves it Ready is never put at risk of Draft by a test).
 */

export function DestinationStateChip({ readiness }: { readiness: DestinationReadinessView }) {
  const chip = chipOf(readiness);
  return (
    <SkyBadge
      appearance={chip.appearance}
      color="subtle"
      size="medium"
      testId="destination-state-chip"
      aria-label={`${chip.label}. ${chip.hint}`}
    >
      {chip.label}
    </SkyBadge>
  );
}

/** Said for a carried Ready destination, with Check now beside it. */
export const CARRIED_LINE =
  "Imports ran with these settings before testing existed. Press Check now to test them.";

/** #1218: the same fact for a reader who has no Check now button. */
export const CARRIED_LINE_READER =
  "Imports ran with these settings before testing existed. Ask a platform admin to check them.";

/** Shown while a test runs; the runner may take up to the probe's budget. */
export const TESTING_LABEL = "Testing... this can take up to a minute.";

/** The sentence, next step and SQL of a failing answer. */
function Advice({
  sentence,
  nextStep,
  sql,
  testId,
}: {
  sentence: string;
  nextStep: string | null;
  sql: readonly string[];
  testId: string;
}) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <SkyText as="p" size={200}>
        {nextStep ? `${sentence} ${nextStep}` : sentence}
      </SkyText>
      {sql.length > 0 ? (
        <SkyCodeBlock
          code={sql.join("\n")}
          language="sql"
          title="Run in Snowflake"
          ariaLabel="SQL for your Snowflake admin"
          testId={`${testId}-sql`}
        />
      ) : null}
    </div>
  );
}

/** "Tested 13 Oct 2026, 09:12 by Ada: passed.", or "Never tested." */
function lastTestLine(
  readiness: DestinationReadinessView,
  zone: string,
  canWrite: boolean,
): string | null {
  if (readiness.state === "not_set" || isComingSoon(readiness)) return null;
  const last = readiness.lastTest;
  if (!last) {
    return readiness.state === "ready" && readiness.source === "carried"
      ? `Never tested. ${canWrite ? CARRIED_LINE : CARRIED_LINE_READER}`
      : "Never tested.";
  }
  const by = last.by ? ` by ${last.by}` : "";
  return `Tested ${formatInstant(last.at, zone)}${by}: ${last.outcome}.`;
}

/** The answer of a Test or a Check now pressed on this page. */
function resultLine(result: DestinationTestView, zone: string): string {
  const at = formatInstant(result.testedAt, zone);
  if (result.mode === "check") {
    return result.passed
      ? `Check now ${at}: OK. The destination stays Ready.`
      : `Check now ${at}: warning. The destination stays Ready and keeps running.`;
  }
  return `Tested ${at}: ${result.passed ? "passed" : "failed"}.`;
}

/** The sentence the driver gave back (code, query id), or null when it gave neither. */
function driverLine(driver: DestinationTestView["driver"]): string | null {
  if (!driver?.code && !driver?.queryId) return null;
  return [
    driver.code ? `Code ${driver.code}` : null,
    driver.queryId ? `Query id ${driver.queryId}` : null,
  ]
    .filter(Boolean)
    .join(". ");
}

/** What a Test or Check now pressed on this page answered. */
function ResultBlock({ result, zone }: { result: DestinationTestView; zone: string }) {
  const driver = driverLine(result.driver);
  return (
    <div className="space-y-2" data-testid="destination-test-result">
      <SkyText as="p" size={200}>
        {resultLine(result, zone)}
      </SkyText>
      {result.passed ? (
        <SkyText as="p" size={200} color="muted">
          {result.sentence}
        </SkyText>
      ) : (
        <Advice
          sentence={result.sentence}
          nextStep={result.nextStep}
          sql={result.sql}
          testId="destination-test-advice"
        />
      )}
      {driver ? (
        <SkyText as="p" size={200} color="muted" testId="destination-test-driver">
          {driver}
        </SkyText>
      ) : null}
    </div>
  );
}

/** The last recorded test and the stored advice, or nothing when there is neither. */
function LastTestBlock({
  readiness,
  last,
}: {
  readiness: DestinationReadinessView;
  last: string | null;
}) {
  if (!last && !readiness.advice) return null;
  return (
    <div className="space-y-2" data-testid="destination-last-test">
      {last ? (
        <SkyText as="p" size={200}>
          {last}
        </SkyText>
      ) : null}
      {readiness.advice ? (
        <Advice
          sentence={readiness.advice.sentence}
          nextStep={readiness.advice.nextStep}
          sql={readiness.advice.sql}
          testId="destination-last-advice"
        />
      ) : null}
    </div>
  );
}

/** Save and test when the form changed, Check now on a Ready destination, else Test. */
function ActionButton({
  readiness,
  dirty,
  running,
  onTest,
  onSaveAndTest,
}: {
  readiness: DestinationReadinessView;
  dirty: boolean;
  running: boolean;
  onTest: (mode: "test" | "check") => void;
  onSaveAndTest: () => void;
}) {
  if (dirty) {
    return (
      <SkyButton
        appearance="primary"
        testId="destination-save-and-test"
        disabled={running}
        loading={running}
        onClick={onSaveAndTest}
      >
        Save and test
      </SkyButton>
    );
  }
  if (readiness.state === "ready") {
    return (
      <SkyButton
        appearance="secondary"
        testId="destination-check-now"
        disabled={running}
        loading={running}
        onClick={() => onTest("check")}
      >
        Check now
      </SkyButton>
    );
  }
  return (
    <SkyButton
      appearance="primary"
      testId="destination-test"
      disabled={running}
      loading={running}
      onClick={() => onTest("test")}
    >
      Test
    </SkyButton>
  );
}

/** Beside the button: that a test is running, or that Test creates the schema. */
function ActionNote({
  readiness,
  dirty,
  running,
}: {
  readiness: DestinationReadinessView;
  dirty: boolean;
  running: boolean;
}) {
  if (running) {
    return (
      <SkyText size={200} color="muted" testId="destination-test-note">
        {TESTING_LABEL}
      </SkyText>
    );
  }
  // Beside every action that may run Test mode (Test, and Save and test, which tests a
  // save that leaves the destination Draft), never beside Check now alone.
  if (dirty || readiness.state !== "ready") {
    return (
      <SkyText size={200} color="muted" testId="destination-test-note">
        {TEST_CREATES_SCHEMA_NOTE}
      </SkyText>
    );
  }
  return null;
}

export function DestinationTestPanel({
  readiness,
  result,
  canWrite,
  dirty,
  running,
  failure,
  onDismissFailure,
  onTest,
  onSaveAndTest,
}: {
  readiness: DestinationReadinessView;
  /** The last Test or Check now pressed on this page, until the next save. */
  result: DestinationTestView | null;
  canWrite: boolean;
  /** The form differs from the saved destination: Save and test, not Test. */
  dirty: boolean;
  running: boolean;
  failure: InlineFailure | null;
  onDismissFailure: () => void;
  onTest: (mode: "test" | "check") => void;
  onSaveAndTest: () => void;
}) {
  const zone = useDisplayTz();
  const reason = readinessReasonLine(readiness);
  const last = result ? null : lastTestLine(readiness, zone, canWrite);
  return (
    <section
      className="space-y-3 rounded-md border border-border p-3"
      aria-label="Destination test"
      data-testid="destination-test-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <SkyText weight="semibold">Destination state</SkyText>
        <DestinationStateChip readiness={readiness} />
        {reason ? (
          <SkyText size={200} color="muted" testId="destination-state-reason">
            {reason}
          </SkyText>
        ) : null}
      </div>

      {result ? (
        <ResultBlock result={result} zone={zone} />
      ) : (
        <LastTestBlock readiness={readiness} last={last} />
      )}

      <InlineErrorBar
        error={failure}
        title={failure?.retryable ? undefined : "The test could not run"}
        onDismiss={onDismissFailure}
        testId="destination-test-error"
      />

      {canWrite && !isComingSoon(readiness) ? (
        <div className="flex flex-wrap items-center gap-3">
          <ActionButton
            readiness={readiness}
            dirty={dirty}
            running={running}
            onTest={onTest}
            onSaveAndTest={onSaveAndTest}
          />
          <ActionNote readiness={readiness} dirty={dirty} running={running} />
        </div>
      ) : null}
    </section>
  );
}
